import { emptySplitApi as api } from "./emptyApi"
import type { Asset } from "./assetsApi"

export type CampaignType = "product" | "collection" | "brand"

export interface ScrapeAssetsRequest {
  campaignType: CampaignType
  productUrl?: string
  homepageUrl: string
  aboutUrl?: string
  tags?: string[]
  productDescription?: string
  includeReviews?: boolean
  brandFont?: string
  logoUrl?: string
  includeEndcard?: boolean
}

export interface ScrapeTextRequest {
  campaignType: CampaignType
  productUrl?: string
  homepageUrl: string
  aboutUrl?: string
  includeReviews: boolean
}

export interface ScrapeTextResponse {
  reviews: ScrapedReview[]
  text: ScrapedText[]
}

export interface ScrapedImage {
  id: string
  url: string
  thumbnailUrl?: string
  type: "image" | "video" | "gif"
  category: string
  color?: string
  selected: boolean
  sourceUrl?: string
  assetId?: string // Asset ID from asset management system
}

export interface ScrapedReview {
  id: string
  author: string
  text: string
  rating: number
  selected: boolean
  sourceUrl?: string
}

export interface ScrapedText {
  id: string
  content: string
  type: "heading" | "paragraph" | "list_item" | "other"
  selected: boolean
  sourceUrl?: string
}

export interface ScrapeAssetsResponse {
  images: ScrapedImage[]
  reviews: ScrapedReview[]
  text: ScrapedText[]
}

export interface GenerateAdCopyRequest {
  sourceText: string[]
  sourceReviews: string[]
  campaignType: CampaignType
  tags: string[]
  productDescription: string
}

export interface GeneratedAdCopy {
  meta: {
    primary: string[]
    headline: string[]
    description: string[]
  }
  tiktok: {
    caption: string[]
  }
  youtube: {
    headline: string[]
    description: string[]
  }
  pmax: {
    headline: string[]
    longHeadline: string[]
    description: string[]
  }
}

export interface GenerateImageRequest {
  /**
   * Asset library ids — the backend resolves each id to bytes server-side.
   * Never ship base64 over the wire.
   */
  referenceAssetIds: string[]
  prompt: string
  aspectRatio?: string
  outputResolution?: string
  /**
   * When set, the backend persists the generated bytes server-side as a
   * GEN_AI child of this asset and returns the saved asset in `asset`. Use
   * this instead of `fetch(data:)` + `/assets/upload` on the client — that
   * client-side fetch of the data URL is blocked by the production CSP/proxy
   * allow-list (the extend flow worked locally but failed on prod).
   */
  parentAssetId?: string
  /** Marker stored on the persisted asset. Defaults to "ai_extend". */
  generatedBy?: string
  /** Placement slot this variant fills (used with `parentAssetId`). */
  placementSlot?: "landscape" | "portrait" | "stories" | "square"
  /**
   * Feed `item_group_id` the generated asset belongs to. When set, the
   * backend persists the asset (even without `parentAssetId`) and tags it
   * into that product's feed pool.
   */
  productRefId?: string
}

export interface GenerateImageResponse {
  /** The generated image as a data URL (handy for an immediate preview). */
  image: string
  /**
   * Present when `parentAssetId` was passed — the persisted GEN_AI asset.
   * Prefer this over re-uploading `image` from the client.
   */
  asset?: Asset
}

export type VeoModel = "veo-3.1-fast-generate-001"

export type VeoPersonGeneration = "allow_all" | "allow_adult"

export interface GenerateMotionRequest {
  /** Asset library id OR an allow-listed media URL — resolved server-side. */
  assetId: string
  /**
   * Additional reference images (ids or URLs) beyond the first frame. Veo 3.1
   * accepts these alongside the source frame, so one call can be driven by
   * several product shots instead of animating a single still.
   */
  referenceAssetIds?: string[]
  /** Product facts injected into the prompt server-side. */
  productContext?: string
  /** Brand design direction injected into the prompt server-side. */
  brandContext?: string
  prompt: string
  aspectRatio: "9:16" | "16:9"
  model?: VeoModel
  durationSeconds?: "4" | "6" | "8"
  resolution?: "720p" | "1080p" | "4k"
  negativePrompt?: string
  enhancePrompt?: boolean
  personGeneration?: VeoPersonGeneration
  numberOfVideos?: number
  seed?: number
}

export interface GenerateMotionResponse {
  operationId: string
}

export interface MotionStatusRequest {
  operationId: string
}

export interface MotionStatusResponse {
  status: "processing" | "completed" | "failed"
  videoUris?: string[]
  error?: string
}

export interface ProxyImageRequest {
  url: string
}

export interface ProxyImageResponse {
  base64: string
}

export interface ProxyVideoRequest {
  videoUri: string
}

export interface ProxyVideoResponse {
  videoUrl: string
}

export interface UploadMotionVideoRequest {
  videoUri: string
  parentAssetId?: string
  aspectRatio?: string
  metadata?: Record<string, unknown>
  /** Feed `item_group_id` — tags the saved clip into that product's pool. */
  productRefId?: string
}

/* ---------------------------------------------------------------
 * AdCreative — drafts saved from AdForge, listed in AdBlaster
 * ------------------------------------------------------------- */

export type AdCreativeFormat = "SINGLE_IMAGE" | "SINGLE_VIDEO" | "CAROUSEL"

/** Mirrors `MediaAsset` in apps/api/src/dto/paid-media/ad.dto.ts */
export interface AdCreativeMediaAsset {
  type: "image" | "video"
  url: string
  assetId?: string
  thumbnailUrl?: string
  caption?: string
  link?: string
  width?: number
  height?: number
}

/** Mirrors `AdCreative` body in CreateAdDto */
export interface AdCreativeContent {
  primaryText: string
  headline?: string
  description?: string
  destinationUrl?: string
  displayLink?: string
  callToAction: string
  mediaAssets: AdCreativeMediaAsset[]
  placementAssets?: Record<string, AdCreativeMediaAsset>
  placementAssetsByCard?: Record<string, Record<string, AdCreativeMediaAsset>>
  existingCreativeId?: string
}

export interface AdCreativeTracking {
  urlParameters?: Record<string, string>
  pixelId?: string
  conversionIds?: string[]
}

/**
 * Structured tag selection carried alongside a creative: tag-category slug →
 * TagOption ids. This is the same selection that composed the ad name in the
 * "Build ad name from tags" modal; the backend turns it into TagAssignments on
 * every ad launched from the creative.
 */
export type AdCreativeTagSelection = Record<string, string[]>

export interface CreateAdCreativeRequest {
  name: string
  format: AdCreativeFormat
  creative: AdCreativeContent
  tracking?: AdCreativeTracking
  platformSpecific?: Record<string, unknown>
  pageId?: string
  tags?: AdCreativeTagSelection
  /**
   * Feed products this creative is marked for (item_group_id). When present,
   * the backend syncs MANUAL AssetProductTags on every media asset to exactly
   * this list — send [] to clear, omit to keep whatever's tagged.
   */
  productRefIds?: string[]
  sourceAdId?: string
  sourcePostId?: string
}

export type UpdateAdCreativeRequest = Partial<CreateAdCreativeRequest>

/** Shape returned by GET /ad-forge/creatives — flat row + `_count.ads` for the AdBlaster Creatives-tab badge. */
export interface AdCreative {
  id: string
  companyId: string
  name: string
  format: AdCreativeFormat
  platform: "META" | "TIKTOK" | "YOUTUBE" | "GOOGLE" | "LINKEDIN"
  primaryText: string
  headline: string | null
  description: string | null
  destinationUrl: string | null
  displayLink: string | null
  callToAction: string
  mediaAssets: AdCreativeMediaAsset[]
  placementAssets: Record<string, AdCreativeMediaAsset> | null
  placementAssetsByCard: Record<
    string,
    Record<string, AdCreativeMediaAsset>
  > | null
  tracking: AdCreativeTracking | null
  pageId: string | null
  platformSpecific: Record<string, unknown> | null
  tags: AdCreativeTagSelection | null
  sourceAdId: string | null
  sourcePostId: string | null
  approvalStatus: AdCreativeApprovalStatus
  reviewNote: string | null
  reviewedById: string | null
  reviewedAt: string | null
  createdById: string | null
  createdAt: string
  updatedAt: string
  /** Saved "Feed products" selection, derived server-side from the media
   * assets' MANUAL tags (URL-only media resolved by S3 key). Present on the
   * detail fetch only. */
  feedProducts?: Array<{ productRefId: string; title: string }>
  _count: { ads: number }
}

/** Mirrors the Prisma AdCreativeApprovalStatus enum. */
export type AdCreativeApprovalStatus =
  | "DRAFT"
  | "PENDING_APPROVAL"
  | "APPROVED"
  | "CHANGES_REQUESTED"
  | "REJECTED"

/** Curated, public-safe creative returned by the /review/:token page endpoints. */
export interface PublicReviewCreative {
  id: string
  name: string
  format: AdCreativeFormat
  platform: AdCreative["platform"]
  primaryText: string
  headline: string | null
  description: string | null
  callToAction: string
  destinationUrl: string | null
  mediaAssets: AdCreativeMediaAsset[]
  placementAssets: Record<string, AdCreativeMediaAsset> | null
  placementAssetsByCard: Record<
    string,
    Record<string, AdCreativeMediaAsset>
  > | null
  approvalStatus: AdCreativeApprovalStatus
  reviewNote: string | null
  reviewedByName: string | null
  reviewedAt: string | null
}

/** Action a public reviewer can take on the shared link. */
export type PublicReviewAction = "approve" | "reject" | "request-changes"

export interface BlastAdCreativeRequest {
  target_adsets: { meta?: string[]; tiktok?: string[] }
  overrides?: {
    destinationUrl?: string
    headline?: string
    description?: string
    primaryText?: string
    callToAction?: string
    adName?: string
  }
}

/**
 * Company-level AdForge defaults. One row in `ctaRules` binds a CTA to a
 * specific Meta objective + optimization-goal pair, so the AdForge UI can
 * pre-select the right CTA based on the destination ad set's targeting.
 */
export interface AdForgeDefaultCtaRule {
  objective: string
  optimizationGoal: string
  callToAction: string
}

/** One ordered slot in the ad-name convention, bound to a tag category. */
export interface AdNamingPosition {
  slug: string
}

/**
 * The company's ad-naming convention in structured form. Ordered positions plus
 * a separator, which makes names machine-readable in BOTH directions: compose
 * one from tags (AdForge), and parse an existing one back into tags by position
 * (`parseAdName`). `nameTemplate` is derived from this server-side on save.
 */
export interface AdNamingConvention {
  separator: string
  positions: AdNamingPosition[]
  prefix?: string
  suffix?: string
}

export interface AdForgeDefaults {
  destinationUrl?: string
  /**
   * The company's UTM/URL-parameter pattern for paid ads. Applied per
   * creative at launch (TrackingSpec.urlParameters → Meta url_tags); agents
   * read it via the MCP adforge_setup tool so they never guess the pattern.
   */
  urlParameters?: Record<string, string>
  /**
   * Fallback CTA when no objective+goal rule matches the destination ad set
   * — also used as the initial picker value on fresh AdForge drafts (where
   * no destination is known yet). Without a company override, the editor's
   * built-in fallback is `LEARN_MORE`.
   */
  defaultCta?: string
  ctaRules: AdForgeDefaultCtaRule[]
  /**
   * Ad-name template for the "Build ad name from tags" composer. `{slug}`
   * placeholders (keyed by tag-category slug) are substituted with the
   * selected tag labels; everything else is a literal. Empty when unset, in
   * which case the composer falls back to a plain delimiter join.
   *
   * Derived from `adNaming` whenever that is set — edit the convention in
   * Company Settings rather than this string.
   */
  nameTemplate?: string
  adNaming?: AdNamingConvention
}

/**
 * PUT body for /ad-forge/defaults. Every field is optional — undefined keys
 * leave the existing server-side value untouched. Pass an empty `ctaRules`
 * array to wipe the matrix.
 */
export interface UpdateAdForgeDefaultsRequest {
  destinationUrl?: string
  /** UTM pattern; pass `{}` to clear, omit to leave unchanged. */
  urlParameters?: Record<string, string>
  defaultCta?: string
  ctaRules?: AdForgeDefaultCtaRule[]
  /**
   * `{slug}` placeholder template; pass `""` to clear it. Sending this WITHOUT
   * `adNaming` drops the structured convention — the server keeps exactly one
   * of the two authoritative.
   */
  nameTemplate?: string
  /** Structured convention; the server derives `nameTemplate` from it. */
  adNaming?: AdNamingConvention
}

/* ---------------------------------------------------------------
 * Ad-name parsing — the reverse of the composer, for auto-tagging
 * ------------------------------------------------------------- */

export interface ParseAdNameRequest {
  adName: string
  /**
   * Create a TagOption for tokens that don't match an existing one. Omit for a
   * dry run — the settings preview must never mutate the taxonomy.
   */
  create?: boolean
}

export type ParsedAdNameStatus =
  | "matched"
  | "created"
  | "would_create"
  | "unknown_category"

export interface ParsedAdNameToken {
  index: number
  slug: string
  categoryLabel?: string
  token: string
  optionId?: string
  optionLabel?: string
  status: ParsedAdNameStatus
}

export interface ParseAdNameResult {
  adName: string
  tokens: ParsedAdNameToken[]
  /** Positions the name had no token for. */
  missingSlugs: string[]
  /** Segments beyond the last position — convention/name mismatch. */
  extraTokens: string[]
  /** Category slug -> TagOption ids, ready for `AdCreative.tags`. */
  tags: Record<string, string[]>
}

/* ---------------------------------------------------------------
 * Brand Kit — company design system fed to the AI overlay canvas
 * ------------------------------------------------------------- */

export interface BrandKitColors {
  primary?: string
  secondary?: string
  accent?: string
  background?: string
  text?: string
  extras?: string[]
}

export interface BrandKitFonts {
  heading?: string
  body?: string
}

/**
 * A company-owned font file. Uploaded via POST /ad-forge/brand-kit/fonts
 * straight to S3 (deliberately NOT an asset-library entry).
 */
export interface BrandKitCustomFont {
  /** Family name the font registers under (usable in fontFamily). */
  family: string
  /** Public URL of the uploaded font file. */
  url: string
}

/**
 * Company Brand Kit stored at `company.metadata.settings.brandKit`. Injected
 * server-side into every /ad-forge/overlay-canvas call so both fresh designs
 * and AI edits are constrained by the company design system.
 */
export interface BrandKit {
  colors?: BrandKitColors
  logoAssetId?: string
  fonts?: BrandKitFonts
  customFonts?: BrandKitCustomFont[]
  dos: string[]
  donts: string[]
  tone?: string
}

/** PUT body — undefined preserves, "" / [] clears; colors/fonts replace whole. */
export interface UpdateBrandKitRequest {
  colors?: BrandKitColors
  logoAssetId?: string
  fonts?: BrandKitFonts
  customFonts?: BrandKitCustomFont[]
  dos?: string[]
  donts?: string[]
  tone?: string
}

/* ---------------------------------------------------------------
 * Overlay canvas — one AI round-trip on the shared canvas doc
 * ------------------------------------------------------------- */

/**
 * Mirrors `OverlayDoc` (v2, flow layout) in
 * apps/api/src/services/ad-forge/overlay-canvas.util.ts — the server
 * sanitizes every AI response into this shape. No absolute coordinates:
 * sections are flex containers anchored on the canvas; items stack inside
 * them, so overlap is impossible. A shared solver turns the structure into
 * boxes for the editor and the exporter.
 */
export interface OverlayDoc {
  version: 2
  canvas: { width: number; height: number }
  sections: OverlaySection[]
}

export interface OverlayGradient {
  from: string
  to: string
  direction: "vertical" | "horizontal" | "diagonal"
}

export interface OverlaySection {
  id: string
  anchor: "top" | "center" | "bottom"
  direction: "column" | "row"
  /** Cross-axis alignment (horizontal placement for columns). */
  align: "start" | "center" | "end"
  /** Main-axis distribution — meaningful for rows. */
  justify: "start" | "center" | "end" | "space-between"
  gap: number
  paddingX: number
  /** Inset from the anchored edge. */
  paddingY: number
  /** Full-width band painted behind the section. */
  background?: {
    color: string
    opacity?: number
    radius?: number
    gradient?: OverlayGradient
  }
  items: OverlayItem[]
}

/**
 * Optional absolute placement. When set (the designer dragged the item), the
 * item renders at this top-left position and is EXCLUDED from its section's
 * flow. Cleared ("docked") → the item re-joins the stack.
 *
 * Percentages of the canvas (0-100), NOT px — ratio-independent, so a
 * free-placed item keeps its relative spot across canvas sizes without any
 * rescaling step.
 */
export interface OverlayItemPosition {
  xPct: number
  yPct: number
}

export interface OverlayTextItem {
  id: string
  type: "text"
  position?: OverlayItemPosition
  text: string
  fontFamily: string
  fontWeight: number
  fontSize: number
  color: string
  lineHeight: number
  uppercase?: boolean
  underline?: boolean
  letterSpacing?: number
  shadow?: boolean
  textAlign?: "left" | "center" | "right"
  /** Wrap width as % of the section content width (10-100). */
  maxWidthPct?: number
  /**
   * Box/pill hugging the text. Filled button = opaque color; outline button
   * = opacity 0 plus borderColor/borderWidth.
   */
  background?: {
    color: string
    paddingX: number
    paddingY: number
    radius: number
    opacity?: number
    borderColor?: string
    borderWidth?: number
  }
}

export interface OverlayLogoItem {
  id: string
  type: "logo"
  position?: OverlayItemPosition
  width: number
  height: number
}

/** Thin accent bar / underline rule. */
export interface OverlayDividerItem {
  id: string
  type: "divider"
  position?: OverlayItemPosition
  width: number
  height: number
  color: string
  radius?: number
}

export interface OverlaySpacerItem {
  id: string
  type: "spacer"
  position?: OverlayItemPosition
  size: number
}

export type OverlayItem =
  | OverlayTextItem
  | OverlayLogoItem
  | OverlayDividerItem
  | OverlaySpacerItem

export interface OverlayCanvasTurn {
  role: "user" | "ai"
  text: string
}

export interface OverlayCanvasRequest {
  /** Base media asset — attached server-side so the AI sees the visual. */
  assetId?: string
  canvasWidth: number
  canvasHeight: number
  /** The current document when editing; omit for a fresh design. */
  currentDoc?: OverlayDoc
  instruction: string
  preset?: string
  /**
   * Recent conversation turns (oldest first, max 12) so iterative
   * instructions ("undo that", "same but bolder") resolve correctly.
   */
  history?: OverlayCanvasTurn[]
  /**
   * Id of the section/item the instruction targets (the editor selection) —
   * the AI focuses its edit there and leaves the rest untouched.
   */
  targetId?: string
}

export interface OverlayCanvasResponse {
  doc: OverlayDoc
  notes?: string
}

/**
 * A saved, reusable overlay layout — designed first on the overlay canvas,
 * then selected while creating an ad and baked onto the ad's media.
 */
export interface OverlayLayout {
  id: string
  companyId: string
  name: string
  /** Display label, e.g. "4:5" — the pixel space lives in `doc.canvas`. */
  aspectRatio: string
  doc: OverlayDoc
  createdById: string | null
  createdAt: string
  updatedAt: string
}

export interface CreateOverlayLayoutRequest {
  name: string
  aspectRatio?: string
  doc: OverlayDoc
}

export interface UpdateOverlayLayoutRequest {
  name?: string
  aspectRatio?: string
  doc?: OverlayDoc
}

/**
 * Launching a saved draft is async now — the endpoint enqueues an `ad-blast`
 * job (`adCreative` unit) and returns immediately. Progress + completion surface
 * via the Jobs notification feed; poll GET /jobs/:id with the returned id for the
 * per-ad-set result. (The AdBlaster launch flow batches drafts into one job via
 * `triggerAdBlast` rather than calling this single-draft endpoint directly.)
 */
export interface BlastAdCreativeResponse {
  jobRunId: string
  status: string
}

const injectedRtkApi = api.injectEndpoints({
  endpoints: build => ({
    adForgeScrapeAssets: build.mutation<
      ScrapeAssetsResponse,
      ScrapeAssetsRequest
    >({
      query: body => ({
        url: `/ad-forge/scrape`,
        method: "POST",
        body,
      }),
    }),
    adForgeScrapeText: build.mutation<ScrapeTextResponse, ScrapeTextRequest>({
      query: body => ({
        url: `/ad-forge/scrape-text`,
        method: "POST",
        body,
      }),
    }),
    adForgeGenerateAdCopy: build.mutation<
      GeneratedAdCopy,
      GenerateAdCopyRequest
    >({
      query: body => ({
        url: `/ad-forge/generate-copy`,
        method: "POST",
        body,
      }),
    }),
    adForgeGenerateImage: build.mutation<
      GenerateImageResponse,
      GenerateImageRequest
    >({
      query: body => ({
        url: `/ad-forge/generate-image`,
        method: "POST",
        body,
      }),
    }),
    adForgeGenerateMotion: build.mutation<
      GenerateMotionResponse,
      GenerateMotionRequest
    >({
      query: body => ({
        url: `/ad-forge/generate-motion`,
        method: "POST",
        body,
      }),
    }),
    adForgeMotionStatus: build.query<MotionStatusResponse, MotionStatusRequest>(
      {
        query: ({ operationId }) => ({
          url: `/ad-forge/generate-motion/${operationId}`,
        }),
      },
    ),
    adForgeProxyImage: build.mutation<ProxyImageResponse, ProxyImageRequest>({
      query: body => ({
        url: `/ad-forge/proxy-image`,
        method: "POST",
        body,
      }),
    }),
    adForgeProxyVideo: build.mutation<ProxyVideoResponse, ProxyVideoRequest>({
      query: body => ({
        url: `/ad-forge/proxy-video`,
        method: "POST",
        body,
      }),
    }),
    adForgeUploadMotionVideo: build.mutation<Asset, UploadMotionVideoRequest>({
      query: body => ({
        url: `/ad-forge/upload-motion-video`,
        method: "POST",
        body,
      }),
    }),

    /* ----------------- AdCreative CRUD + blast ----------------- */

    adForgeCreateCreative: build.mutation<AdCreative, CreateAdCreativeRequest>({
      query: body => ({
        url: `/ad-forge/creatives`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["AdCreative"],
    }),

    adForgeListCreatives: build.query<AdCreative[], { search?: string } | void>(
      {
        query: arg => ({
          url: `/ad-forge/creatives`,
          params: arg?.search ? { search: arg.search } : undefined,
        }),
        providesTags: result =>
          result
            ? [
                ...result.map(c => ({ type: "AdCreative" as const, id: c.id })),
                { type: "AdCreative" as const, id: "LIST" },
              ]
            : [{ type: "AdCreative" as const, id: "LIST" }],
      },
    ),

    adForgeGetCreative: build.query<AdCreative, string>({
      query: id => `/ad-forge/creatives/${id}`,
      // AssetProductTags too: the detail carries `feedProducts` derived from
      // the media assets' MANUAL tags, so tagging/untagging elsewhere (drawer
      // popover, feed media editor) must refetch it.
      providesTags: (_result, _error, id) => [
        { type: "AdCreative", id },
        "AssetProductTags",
      ],
    }),

    adForgeUpdateCreative: build.mutation<
      AdCreative,
      { id: string; body: UpdateAdCreativeRequest }
    >({
      query: ({ id, body }) => ({
        url: `/ad-forge/creatives/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "AdCreative", id },
        { type: "AdCreative", id: "LIST" },
      ],
    }),

    adForgeDeleteCreative: build.mutation<{ ok: boolean }, string>({
      query: id => ({
        url: `/ad-forge/creatives/${id}`,
        method: "DELETE",
      }),
      // Drop the row from every cached list as soon as the DELETE succeeds —
      // the tag invalidation below refetches for eventual consistency, but
      // without this patch the deleted row lingers until that round trip
      // lands. Pessimistic (after queryFulfilled), so a failed delete never
      // makes a row vanish.
      async onQueryStarted(id, { dispatch, getState, queryFulfilled }) {
        try {
          await queryFulfilled
        } catch {
          return
        }
        // The list is cached once per search arg — patch every cached entry.
        for (const arg of injectedRtkApi.util.selectCachedArgsForQuery(
          getState(),
          "adForgeListCreatives",
        )) {
          dispatch(
            injectedRtkApi.util.updateQueryData(
              "adForgeListCreatives",
              arg,
              draft => {
                const idx = draft.findIndex(c => c.id === id)
                if (idx !== -1) draft.splice(idx, 1)
              },
            ),
          )
        }
      },
      invalidatesTags: (_result, _error, id) => [
        { type: "AdCreative", id },
        { type: "AdCreative", id: "LIST" },
      ],
    }),

    /** Submit a creative for approval (DRAFT / CHANGES_REQUESTED → PENDING_APPROVAL). */
    adForgeSubmitCreative: build.mutation<AdCreative, string>({
      query: id => ({
        url: `/ad-forge/creatives/${id}/submit`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "AdCreative", id },
        { type: "AdCreative", id: "LIST" },
      ],
    }),

    /** Approve a creative awaiting review (PENDING_APPROVAL → APPROVED). */
    adForgeApproveCreative: build.mutation<
      AdCreative,
      { id: string; note?: string }
    >({
      query: ({ id, note }) => ({
        url: `/ad-forge/creatives/${id}/approve`,
        method: "POST",
        body: { note },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "AdCreative", id },
        { type: "AdCreative", id: "LIST" },
      ],
    }),

    /** Send a creative back for changes (PENDING_APPROVAL → CHANGES_REQUESTED). */
    adForgeRequestChangesCreative: build.mutation<
      AdCreative,
      { id: string; note?: string }
    >({
      query: ({ id, note }) => ({
        url: `/ad-forge/creatives/${id}/request-changes`,
        method: "POST",
        body: { note },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "AdCreative", id },
        { type: "AdCreative", id: "LIST" },
      ],
    }),

    /** Get (creating if needed) the public review-link token for a creative. */
    adForgeGetReviewLink: build.mutation<{ token: string }, string>({
      query: id => ({
        url: `/ad-forge/creatives/${id}/review-link`,
        method: "POST",
      }),
    }),

    /** Public (unauthenticated) view of a creative behind a review token. */
    adForgeGetPublicReview: build.query<PublicReviewCreative, string>({
      query: token => `/public/ad-review/${token}`,
    }),

    /** Public (unauthenticated) review decision — approve / reject / request-changes. */
    adForgePublicReview: build.mutation<
      PublicReviewCreative,
      {
        token: string
        action: PublicReviewAction
        reviewerName?: string
        note?: string
      }
    >({
      query: ({ token, action, reviewerName, note }) => ({
        url: `/public/ad-review/${token}/${action}`,
        method: "POST",
        body: { reviewerName, note },
      }),
      // The backend returns the post-transition creative, so seed it straight
      // into the getPublicReview cache. Without this the page keeps rendering the
      // pre-action status/buttons; a second click then 409s with no explanation.
      async onQueryStarted({ token }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(
            injectedRtkApi.util.updateQueryData(
              "adForgeGetPublicReview",
              token,
              () => data,
            ),
          )
        } catch {
          // Mutation rejected (e.g. 409) — leave the cache as-is; the component
          // surfaces the error and a manual refetch reconciles the true state.
        }
      },
    }),

    adForgeBlastCreative: build.mutation<
      BlastAdCreativeResponse,
      { id: string; body: BlastAdCreativeRequest }
    >({
      query: ({ id, body }) => ({
        url: `/ad-forge/creatives/${id}/blast`,
        method: "POST",
        body,
      }),
      // The blast creates Ad rows on Meta and in our DB, so invalidate the
      // Ad list (AdBlaster's Ads tab) AND the creative's launched-count.
      invalidatesTags: (_result, _error, { id }) => [
        { type: "AdCreative", id },
        { type: "AdCreative", id: "LIST" },
        "Ad",
      ],
    }),

    adForgeGetDefaults: build.query<AdForgeDefaults, void>({
      query: () => ({ url: `/ad-forge/defaults` }),
      providesTags: ["AdForgeDefaults"],
    }),

    adForgeUpdateDefaults: build.mutation<
      AdForgeDefaults,
      UpdateAdForgeDefaultsRequest
    >({
      query: body => ({
        url: `/ad-forge/defaults`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["AdForgeDefaults"],
    }),

    /**
     * Split an ad name into tags by position. A dry run by default, so the
     * settings test box can call it on every keystroke without side effects.
     * `create: true` materializes options and therefore invalidates the tag
     * caches; a dry run leaves them alone.
     */
    adForgeParseAdName: build.mutation<ParseAdNameResult, ParseAdNameRequest>({
      query: body => ({
        url: `/ad-forge/defaults/parse-ad-name`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, arg) =>
        arg.create ? [{ type: "TagCategory" as const, id: "LIST" }] : [],
    }),

    adForgeGetBrandKit: build.query<BrandKit, void>({
      query: () => ({ url: `/ad-forge/brand-kit` }),
      providesTags: ["BrandKit"],
    }),

    adForgeUpdateBrandKit: build.mutation<BrandKit, UpdateBrandKitRequest>({
      query: body => ({
        url: `/ad-forge/brand-kit`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["BrandKit"],
    }),

    /** Upload a company-owned font file (woff2/woff/ttf/otf) → { url }. */
    adForgeUploadBrandFont: build.mutation<{ url: string }, File>({
      query: file => {
        const formData = new FormData()
        formData.append("file", file)
        return {
          url: `/ad-forge/brand-kit/fonts`,
          method: "POST",
          body: formData,
        }
      },
    }),

    /** One AI round-trip on the shared overlay canvas (design or edit). */
    adForgeOverlayCanvas: build.mutation<
      OverlayCanvasResponse,
      OverlayCanvasRequest
    >({
      query: body => ({
        url: `/ad-forge/overlay-canvas`,
        method: "POST",
        body,
      }),
    }),

    adForgeListOverlayLayouts: build.query<OverlayLayout[], void>({
      query: () => ({ url: `/ad-forge/overlay-layouts` }),
      providesTags: ["OverlayLayout"],
    }),

    adForgeCreateOverlayLayout: build.mutation<
      OverlayLayout,
      CreateOverlayLayoutRequest
    >({
      query: body => ({
        url: `/ad-forge/overlay-layouts`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["OverlayLayout"],
    }),

    adForgeUpdateOverlayLayout: build.mutation<
      OverlayLayout,
      { id: string; body: UpdateOverlayLayoutRequest }
    >({
      query: ({ id, body }) => ({
        url: `/ad-forge/overlay-layouts/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["OverlayLayout"],
    }),

    adForgeDeleteOverlayLayout: build.mutation<{ ok: boolean }, string>({
      query: id => ({
        url: `/ad-forge/overlay-layouts/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["OverlayLayout"],
    }),
  }),
  overrideExisting: false,
})

export const {
  useAdForgeScrapeAssetsMutation,
  useAdForgeScrapeTextMutation,
  useAdForgeGenerateAdCopyMutation,
  useAdForgeGenerateImageMutation,
  useAdForgeGenerateMotionMutation,
  useLazyAdForgeMotionStatusQuery,
  useAdForgeProxyImageMutation,
  useAdForgeProxyVideoMutation,
  useAdForgeUploadMotionVideoMutation,
  useAdForgeCreateCreativeMutation,
  useAdForgeListCreativesQuery,
  useAdForgeGetCreativeQuery,
  useAdForgeUpdateCreativeMutation,
  useAdForgeDeleteCreativeMutation,
  useAdForgeSubmitCreativeMutation,
  useAdForgeApproveCreativeMutation,
  useAdForgeRequestChangesCreativeMutation,
  useAdForgeGetReviewLinkMutation,
  useAdForgeGetPublicReviewQuery,
  useAdForgePublicReviewMutation,
  useAdForgeBlastCreativeMutation,
  useAdForgeGetDefaultsQuery,
  useAdForgeUpdateDefaultsMutation,
  useAdForgeParseAdNameMutation,
  useAdForgeGetBrandKitQuery,
  useAdForgeUpdateBrandKitMutation,
  useAdForgeUploadBrandFontMutation,
  useAdForgeOverlayCanvasMutation,
  useAdForgeListOverlayLayoutsQuery,
  useAdForgeCreateOverlayLayoutMutation,
  useAdForgeUpdateOverlayLayoutMutation,
  useAdForgeDeleteOverlayLayoutMutation,
} = injectedRtkApi
